import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";

import {
  useGetCachedShiftRouteEstimate,
  useShiftRouteEstimate,
} from "../ArrivalTime/ShiftRouteEstimate/useShiftRouteEstimate";
import { useArrivalTimePoints } from "../ArrivalTime/useArrivalTimePoints";
import { useIsArrivalTimeEligible } from "../ArrivalTime/useIsArrivalTimeEligible";
import { type ModalShiftData } from "../Shift/types";
import { useIsDoubleBookedShift } from "./useIsDoubleBookedShift";

interface UseArrivalTimeDataProps {
  workerId: string;
  shift: ModalShiftData | undefined;
  workplace?: {
    id: string;
    attributes: {
      type?: string;
      location: {
        latitude?: number;
        longitude?: number;
        msa?: string;
      };
    };
  };
  isInstantBook?: boolean;
  resolvedShiftQualification?: string;
}

export function useArrivalTimeData(props: UseArrivalTimeDataProps) {
  const { workerId, shift, workplace, isInstantBook, resolvedShiftQualification } = props;

  const isArrivalTimeEligible = useIsArrivalTimeEligible({
    shiftStartIso: shift?.attributes.start,
    isInstantBook,
    workplaceType: workplace?.attributes.type,
    workplaceId: workplace?.id ?? "",
    workplaceMsa: workplace?.attributes?.location?.msa ?? "",
  });

  // Disable showing arrival time if its a double booked shift
  const { isDoubleBookedShift, isLoading: isLoadingDoubleBookedShift } = useIsDoubleBookedShift({
    workerId,
    shift: {
      id: shift?.id ?? "",
      attributes: {
        start: shift?.attributes.start ?? "",
        workplaceId: workplace?.id ?? "",
      },
    },
    enabled: isArrivalTimeEligible && isDefined(shift) && isDefined(workplace?.id),
  });

  const isRouteEstimateEnabled =
    isDefined(shift) &&
    isDefined(workplace) &&
    isDefined(workerId) &&
    isArrivalTimeEligible &&
    !isDoubleBookedShift;

  useLogEffect(
    APP_V2_APP_EVENTS.SHIFT_DETAILS_ROUTE_ESTIMATE_UNAVAILABLE,
    {
      shiftId: shift?.id,
      workplaceId: workplace?.id,
      workerId,
      isArrivalTimeEligible,
      isDoubleBookedShift,
    },
    {
      enabled: !isRouteEstimateEnabled,
    }
  );

  const { isLoading: isLoadingRouteEstimate, data: fetchedArrivalData } = useShiftRouteEstimate(
    {
      destination: {
        latitude: workplace?.attributes.location.latitude ?? 0,
        longitude: workplace?.attributes.location.longitude ?? 0,
      },
      workerId: workerId ?? "",
      shiftId: shift?.id,
      workplaceId: workplace?.id ?? "",
      shiftStartIso: shift?.attributes.start,
      shiftAuctionEndDate: shift?.shiftAuction?.attributes.endTime,
    },
    {
      enabled: isRouteEstimateEnabled,
    }
  );
  const isRouteEstimateLoadingAndEnabled = isLoadingRouteEstimate && isRouteEstimateEnabled;

  const { data: arrivalTimePoints, isLoading: isLoadingArrivalTimePoints } = useArrivalTimePoints({
    workerId,
    qualification: resolvedShiftQualification,
  });

  // Fall back to cached data when query becomes disabled (e.g., double-booked check flips).
  // This prevents the arrival time card from flickering/disappearing.
  const cachedArrivalData = useGetCachedShiftRouteEstimate(shift?.id);
  const computedArrivalData = fetchedArrivalData ?? cachedArrivalData;

  return {
    computedArrivalData,
    arrivalTimePoints,
    isLoadingArrivalTimePoints,
    isRouteEstimateLoadingAndEnabled,
    isLoadingDoubleBookedShift,
  };
}
